import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "course_settings";

const DAYS_KO = ["일", "월", "화", "수", "목", "금", "토"];

export interface CourseSettings {
  deadline: string;
  monWedStart: string;
  tueThuStart: string;
  monWedEnd: string;
  tueThuEnd: string;
}

const DEFAULTS: CourseSettings = {
  deadline: "2026-08-09T23:59",
  monWedStart: "2026-08-10",
  tueThuStart: "2026-08-11",
  monWedEnd: "2026-09-30",
  tueThuEnd: "2026-10-01",
};

function readStored(): CourseSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        deadline: parsed.deadline || DEFAULTS.deadline,
        monWedStart: parsed.monWedStart || DEFAULTS.monWedStart,
        tueThuStart: parsed.tueThuStart || DEFAULTS.tueThuStart,
        monWedEnd: parsed.monWedEnd || DEFAULTS.monWedEnd,
        tueThuEnd: parsed.tueThuEnd || DEFAULTS.tueThuEnd,
      };
    }
  } catch {
    // fall through
  }
  return DEFAULTS;
}

function writeStored(settings: CourseSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // storage unavailable
  }
}

export function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const dow = DAYS_KO[date.getDay()];
  return `${m}/${d}(${dow})`;
}

export function formatDateRange(startStr: string, endStr: string): string {
  const startParts = startStr.split("-").map(Number);
  const endParts = endStr.split("-").map(Number);
  const start = new Date(startParts[0], startParts[1] - 1, startParts[2]);
  const end = new Date(endParts[0], endParts[1] - 1, endParts[2]);
  const diffDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const weeks = Math.ceil(diffDays / 7);
  return `${formatDate(startStr)} ~ ${formatDate(endStr)}, ${weeks}주`;
}

export function parseDeadlineDate(deadlineStr: string): Date {
  if (deadlineStr.includes("T")) {
    return new Date(deadlineStr);
  }
  return new Date(`${deadlineStr}T23:59:00`);
}

export default function useCourseSettings() {
  const [settings, setSettings] = useState<CourseSettings>(readStored);

  useEffect(() => {
    const onStorage = () => setSettings(readStored());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const updateSettings = useCallback((partial: Partial<CourseSettings>) => {
    const next = { ...readStored(), ...partial };
    writeStored(next);
    setSettings(next);
  }, []);

  return { settings, updateSettings };
}